/**
 * Shared vendor-sync completion helpers for SearchPollingService and result pages.
 *
 * Contract (all products):
 *   vendorSyncComplete =
 *     done === true AND background_refresh_pending !== true
 *     (also accept explicit status === 'completed' when not refreshing)
 *
 * Server contract for OSINT /osint/job:
 *   vendor_sync_complete === result_ready && background_refresh_pending===false
 * Polling should stop only when vendor sync is complete (or deleted / timed out / failed).
 *
 * IMPORTANT: `result_ready` / `done` alone must NOT map to status "completed".
 * Hydrate / cache-fast-path sets done=true while vendor may still be refreshing.
 */

export type SearchVendorStatusLabel =
  | 'Completed'
  | 'Refreshing'
  | 'Pending'
  | 'Error'
  | 'Deleted';

/** Shared completion rule used by advanced / basic / leak / OSINT. */
export function isVendorSyncComplete(job: any): boolean {
  if (!job) {
    return false;
  }
  if (job.deleted === true || job.status === 'deleted') {
    return true;
  }
  if (
    job.status === 'error' ||
    job.status === 'failed' ||
    job.timed_out === true
  ) {
    return true;
  }
  if (job.vendor_sync_complete === true) {
    return true;
  }
  if (job.vendor_sync_complete === false) {
    return false;
  }
  if (job.background_refresh_pending === true || job.status === 'refreshing') {
    return false;
  }
  if (job.status === 'completed') {
    return true;
  }
  // done without an explicit pending refresh (undefined pending counts as not pending)
  if (job.done === true) {
    return true;
  }
  return false;
}

/** First real numeric percent (0–100); null/undefined/'' mean "unknown", not 0. */
export function toPercent(...values: any[]): number | null {
  for (const value of values) {
    if (value == null || value === '') continue;
    const num = Number(value);
    if (Number.isFinite(num) && num >= 0) {
      return Math.min(100, Math.round(num));
    }
  }
  return null;
}

/** Alias kept for existing OSINT imports. */
export function isOsintJobVendorSyncComplete(job: any): boolean {
  return isVendorSyncComplete(job);
}

/** True when cached/hydrated results may be shown but vendor is still running. */
export function isBackgroundRefreshPending(job: any): boolean {
  if (!job || isVendorSyncComplete(job)) {
    return false;
  }
  if (job.background_refresh_pending === true || job.status === 'refreshing') {
    return true;
  }
  if (job.done === true && job.vendor_sync_complete === false) {
    return true;
  }
  return false;
}

/** True when results are usable for early paint (hydrate or full complete). */
export function isSearchResultReady(job: any): boolean {
  if (!job) {
    return false;
  }
  if (job.deleted === true || job.status === 'deleted') {
    return false;
  }
  return (
    Boolean(job.done) ||
    job.status === 'completed' ||
    job.status === 'refreshing' ||
    job.result_ready === true
  );
}

export function getSearchVendorStatusLabel(job: any): SearchVendorStatusLabel {
  if (!job) {
    return 'Pending';
  }
  if (job.deleted === true || job.status === 'deleted') {
    return 'Deleted';
  }
  if (
    job.timed_out === true ||
    job.status === 'error' ||
    job.status === 'failed'
  ) {
    return 'Error';
  }
  if (isVendorSyncComplete(job)) {
    return 'Completed';
  }
  // Refreshing only when hydrate/preview is ready AND vendor still running.
  if (Boolean(job.done) && isBackgroundRefreshPending(job)) {
    return 'Refreshing';
  }
  return 'Pending';
}

/**
 * Normalizes poll responses for SearchPollingService.
 * When `jobs` is present (Industry API + cache fast path), each entry has
 * `result_ready` (row exists / preview) and `vendor_sync_complete` (vendor API finished).
 */
export function mapOsintJobPollResultsFromResponse(response: any): any[] {
  const data = response?.data;
  if (!data || Array.isArray(data)) {
    return Array.isArray(response?.data) ? response.data : [];
  }

  if (Array.isArray(data.jobs) && data.jobs.length > 0) {
    const batchStatus = data.status || 'pending';
    return data.jobs.map((j: any) => {
      const deleted = Boolean(j.deleted || data.deleted || batchStatus === 'deleted');
      const jobDone = Boolean(j.result_ready ?? j.done);
      const refreshPending = Boolean(
        j.background_refresh_pending ?? data.background_refresh_pending,
      );
      const vendorSyncComplete = deleted
        ? true
        : j.vendor_sync_complete !== undefined
          ? Boolean(j.vendor_sync_complete)
          : isVendorSyncComplete({
              done: jobDone,
              status: j.status || batchStatus,
              background_refresh_pending: refreshPending,
            });
      let status = deleted ? 'deleted' : j.status || batchStatus;
      if (!deleted && jobDone && status === 'failed' && !(j.notes || data.notes)) {
        status = 'completed';
      }
      if (!deleted && vendorSyncComplete) {
        status = 'completed';
      } else if (!deleted && !vendorSyncComplete && status === 'completed') {
        // Preview-ready but vendor still running — keep UI in pending/updating
        status = refreshPending || jobDone ? 'refreshing' : 'pending';
      } else if (!deleted && !vendorSyncComplete && refreshPending && jobDone) {
        status = 'refreshing';
      }

      const progress_percent = toPercent(
        j?.progress?.percent,
        j.progress_percent,
        data.progress_percent,
      );

      return {
        job_id: j.job_id,
        done: deleted ? true : jobDone,
        vendor_sync_complete: vendorSyncComplete,
        background_refresh_pending: deleted ? false : refreshPending,
        name_search_id: j.name_search_id ?? data.name_search_id,
        leak_search_id: j.leak_search_id ?? data.leak_search_id,
        basic_search_id: j.basic_search_id ?? data.basic_search_id,
        pan_search_id: j.pan_search_id ?? data.pan_search_id,
        gst_search_id: j.gst_search_id ?? data.gst_search_id,
        phonepe_fastag_search_id:
          j.phonepe_fastag_search_id ?? data.phonepe_fastag_search_id,
        phonepe_upi_search_id:
          j.phonepe_upi_search_id ?? data.phonepe_upi_search_id,
        deleted,
        status,
        progress_percent,
        progress_milestone: j.progress_milestone ?? null,
        emails_ready: Boolean(j.emails_ready),
        found: j.found ?? null,
        vendor_status: j.vendor_status ?? null,
        is_final: deleted ? true : Boolean(j.is_final === true || vendorSyncComplete),
        progress: j.progress ?? null,
        ...(j.notes || data.notes
          ? { notes: j.notes || data.notes }
          : {}),
        ...(j.message || data.message
          ? { message: j.message || data.message }
          : {}),
      };
    });
  }

  if (data.job_ids && Array.isArray(data.job_ids)) {
    const deleted = Boolean(data.deleted || data.status === 'deleted');
    const refreshPending = Boolean(data.background_refresh_pending);
    const vendorSyncComplete = deleted
      ? true
      : data.vendor_sync_complete !== undefined
        ? Boolean(data.vendor_sync_complete)
        : isVendorSyncComplete({
            done: data.done,
            status: data.status,
            background_refresh_pending: refreshPending,
          });
    return data.job_ids.map((jobId: string) => {
      let status = deleted ? 'deleted' : data.status || 'pending';
      if (!deleted && data.done === true && status === 'failed' && !data.notes) {
        status = 'completed';
      }
      if (!deleted && vendorSyncComplete) {
        status = 'completed';
      } else if (!deleted && !vendorSyncComplete && status === 'completed') {
        status =
          refreshPending || data.done === true ? 'refreshing' : 'pending';
      } else if (
        !deleted &&
        !vendorSyncComplete &&
        refreshPending &&
        data.done === true
      ) {
        status = 'refreshing';
      }
      const progress_percent = toPercent(
        data?.progress?.percent,
        data.progress_percent,
      );
      return {
        job_id: jobId,
        done: deleted ? true : data.done || false,
        vendor_sync_complete: vendorSyncComplete,
        background_refresh_pending: deleted ? false : refreshPending,
        name_search_id: data.name_search_id,
        leak_search_id: data.leak_search_id,
        basic_search_id: data.basic_search_id,
        pan_search_id: data.pan_search_id,
        gst_search_id: data.gst_search_id,
        phonepe_fastag_search_id: data.phonepe_fastag_search_id,
        phonepe_upi_search_id: data.phonepe_upi_search_id,
        deleted,
        status,
        progress_percent,
        progress: data.progress ?? null,
        is_final: deleted ? true : Boolean(vendorSyncComplete),
        ...(data.notes ? { notes: data.notes } : {}),
        ...(data.message ? { message: data.message } : {}),
      };
    });
  }

  return [];
}
